import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Fab
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CardGiftcard as GiftShopIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  ShoppingCart as OrdersIcon,
  Inventory as ProductsIcon,
  TrendingUp as StatsIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import adminAuthService from '../services/adminAuthService';
import { API_BASE_URL } from '../config/api';

const GiftShopManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Gift products state
  const [giftProducts, setGiftProducts] = useState([]);
  const [giftProductsLoading, setGiftProductsLoading] = useState(false);
  
  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Gift orders state
  const [giftOrders, setGiftOrders] = useState([]);
  const [giftOrdersLoading, setGiftOrdersLoading] = useState(false);
  
  // Dialog states
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    points_cost: '',
    image: '',
    status: 'active'
  });
  
  // Order dialog states
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderViewMode, setOrderViewMode] = useState('view'); // 'view' or 'edit'
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Statistics
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    totalOrders: 0,
    pendingOrders: 0,
    totalRevenue: 0
  });

  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    if (!adminAuthService.isAuthenticated() || !adminAuthService.isAdmin()) {
      navigate('/admin/login');
      return;
    }

    loadGiftShopData();
  }, [navigate]);

  const loadGiftShopData = async () => {
    setLoading(true);
    setError('');
    
    try {
      await Promise.all([
        loadGiftProducts(),
        loadGiftOrders(),
        loadStats()
      ]);
    } catch (error) {
      setError('فشل في تحميل بيانات متجر الهدايا');
      console.error('Error loading gift shop data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGiftProducts = async () => {
    setGiftProductsLoading(true);
    try {
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_gift_products.php?page=1&limit=100&search=${searchTerm}&status=${filterStatus}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch gift products');
      }
      
      const data = await response.json();
      if (data.success) {
        setGiftProducts(data.data || []);
      } else {
        throw new Error(data.error || 'Failed to load gift products');
      }
    } catch (error) {
      console.error('Error loading gift products:', error);
      setError('فشل في تحميل منتجات الهدايا: ' + error.message);
    } finally {
      setGiftProductsLoading(false);
    }
  };

  const loadGiftOrders = async () => {
    setGiftOrdersLoading(true);
    try {
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_gift_orders.php?page=1&limit=100&search=${searchTerm}&status=${filterStatus}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch gift orders');
      }
      
      const data = await response.json();
      if (data.success) {
        setGiftOrders(data.data || []);
      } else {
        throw new Error(data.error || 'Failed to load gift orders');
      }
    } catch (error) {
      console.error('Error loading gift orders:', error);
      setError('فشل في تحميل طلبات الهدايا: ' + error.message);
    } finally {
      setGiftOrdersLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_gift_stats.php`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch gift shop statistics');
      }
      
      const data = await response.json();
      if (data.success && data.data.overview) {
        setStats({
          totalProducts: data.data.overview.total_products || 0,
          activeProducts: data.data.overview.active_products || 0,
          totalOrders: data.data.overview.total_orders || 0,
          pendingOrders: data.data.overview.pending_orders || 0,
          totalRevenue: data.data.overview.total_points_used || 0
        });
      } else {
        throw new Error(data.error || 'Failed to load statistics');
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      // Keep default mock stats on error
      setStats({
        totalProducts: 0,
        activeProducts: 0,
        totalOrders: 0,
        pendingOrders: 0,
        totalRevenue: 0
      });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;

    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('image', file);

      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/image_optimizer.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();
      if (data.success && data.image_url) {
        return data.image_url;
      } else {
        throw new Error(data.message || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('فشل في رفع الصورة: ' + error.message);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('يرجى اختيار ملف صورة صالح');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
      return;
    }

    setSelectedFile(file);
    const imageUrl = await handleImageUpload(file);
    if (imageUrl) {
      setProductForm(prev => ({ ...prev, image: imageUrl }));
    }
  };

  const handleProductSave = async () => {
    try {
      const token = adminAuthService.getToken();
      const url = editingProduct 
        ? `${API_BASE_URL}/api/admin_gift_products.php`
        : `${API_BASE_URL}/api/admin_gift_products.php`;
      
      const method = editingProduct ? 'PUT' : 'POST';
      const payload = editingProduct 
        ? { ...productForm, id: editingProduct.id }
        : productForm;
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save product');
      }
      
      const data = await response.json();
      if (data.success) {
        // Reload products to get updated data
        await loadGiftProducts();
        await loadStats();
        
        setProductDialogOpen(false);
        setEditingProduct(null);
        setProductForm({
          name: '',
          description: '',
          points_cost: '',
          image: '',
          status: 'active'
        });
        setSelectedFile(null);
      } else {
        throw new Error(data.error || 'Failed to save product');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      setError('فشل في حفظ المنتج: ' + error.message);
    }
  };

  const handleProductEdit = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      points_cost: product.points_cost || '',
      image: product.image,
      status: product.status
    });
    setProductDialogOpen(true);
  };

  const handleProductDelete = async (productId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) {
      try {
        const token = adminAuthService.getToken();
        const response = await fetch(`${API_BASE_URL}/api/admin_gift_products.php`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: productId })
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete product');
        }
        
        const data = await response.json();
        if (data.success) {
          // Reload products and stats
          await loadGiftProducts();
          await loadStats();
        } else {
          throw new Error(data.error || 'Failed to delete product');
        }
      } catch (error) {
        console.error('Error deleting product:', error);
        setError('فشل في حذف المنتج: ' + error.message);
      }
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: 'نشط', color: 'success', icon: <ActiveIcon /> },
      inactive: { label: 'غير نشط', color: 'default', icon: <InactiveIcon /> },
      pending: { label: 'في الانتظار', color: 'warning' },
      confirmed: { label: 'مؤكد', color: 'info' },
      delivered: { label: 'تم التوصيل', color: 'success' },
      cancelled: { label: 'ملغي', color: 'error' }
    };

    const config = statusConfig[status] || { label: status, color: 'default' };
    
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
        sx={{ fontWeight: 'medium' }}
      />
    );
  };

  const formatPrice = (price) => {
    return Number(price || 0).toLocaleString() + ' د.ع';
  };

  // Order handlers
  const handleOrderView = (order) => {
    setSelectedOrder(order);
    setOrderViewMode('view');
    setOrderDialogOpen(true);
  };

  const handleOrderEdit = (order) => {
    setSelectedOrder(order);
    setOrderViewMode('edit');
    setOrderDialogOpen(true);
  };

  const handleOrderDialogClose = () => {
    setOrderDialogOpen(false);
    setSelectedOrder(null);
    setOrderViewMode('view');
  };

  const handleOrderUpdate = async (updatedOrder) => {
    try {
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_gift_orders.php`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: updatedOrder.id,
          status: updatedOrder.status
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update order');
      }

      const data = await response.json();
      if (data.success) {
        // Reload orders and stats
        await loadGiftOrders();
        await loadStats();
        handleOrderDialogClose();
        setError('');
      } else {
        throw new Error(data.error || 'Failed to update order');
      }
    } catch (error) {
      console.error('Error updating order:', error);
      setError('فشل في تحديث الطلب: ' + error.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ar-IQ');
  };

  const filteredProducts = giftProducts.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || product.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredOrders = giftOrders.filter(order => {
    const matchesSearch = order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.order_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.product_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/admin/dashboard')}
            sx={{ mr: 2 }}
          >
            <BackIcon />
          </IconButton>
          <GiftShopIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            إدارة متجر الهدايا
          </Typography>
          <Button
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={loadGiftShopData}
          >
            تحديث
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <CardContent>
                <ProductsIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  {stats.totalProducts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  إجمالي المنتجات
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <CardContent>
                <ActiveIcon sx={{ fontSize: 40, color: '#2e7d32', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                  {stats.activeProducts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  منتجات نشطة
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <CardContent>
                <OrdersIcon sx={{ fontSize: 40, color: '#ed6c02', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ed6c02' }}>
                  {stats.totalOrders}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  إجمالي الطلبات
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <CardContent>
                <Badge badgeContent={stats.pendingOrders} color="error">
                  <OrdersIcon sx={{ fontSize: 40, color: '#d32f2f', mb: 1 }} />
                </Badge>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                  {stats.pendingOrders}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  طلبات معلقة
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <CardContent>
                <StatsIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  {stats.totalRevenue} نقطة
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  إجمالي النقاط المستخدمة
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 'medium',
                fontSize: '1rem',
                '&.Mui-selected': {
                  color: '#1976d2',
                  fontWeight: 'bold'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
                height: 3
              }
            }}
          >
            <Tab
              icon={<ProductsIcon />}
              label="المنتجات"
              iconPosition="start"
              sx={{ px: 4 }}
            />
            <Tab
              icon={<OrdersIcon />}
              label="الطلبات"
              iconPosition="start"
              sx={{ px: 4 }}
            />
          </Tabs>
        </Paper>

        {/* Search and Filter Bar */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="البحث..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>فلتر الحالة</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="فلتر الحالة"
                >
                  <MenuItem value="all">جميع الحالات</MenuItem>
                  {tabValue === 0 ? [
                      <MenuItem key="active" value="active">نشط</MenuItem>,
                      <MenuItem key="inactive" value="inactive">غير نشط</MenuItem>
                  ] : [
                      <MenuItem key="pending" value="pending">في الانتظار</MenuItem>,
                      <MenuItem key="confirmed" value="confirmed">مؤكد</MenuItem>,
                      <MenuItem key="delivered" value="delivered">تم التوصيل</MenuItem>,
                      <MenuItem key="cancelled" value="cancelled">ملغي</MenuItem>
                  ]}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              {tabValue === 0 && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setProductDialogOpen(true)}
                >
                  إضافة منتج
                </Button>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Products Tab */}
        {tabValue === 0 && (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>المنتج</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>النقاط</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>تاريخ الإضافة</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {giftProductsLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : filteredProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                        لا توجد منتجات
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {product.image && (
                              <Box
                                component="img"
                                src={product.image.startsWith('http') ? 
                                  product.image : 
                                  `${API_BASE_URL}/api/uploads/products/${product.image}`
                                }
                                alt={product.name}
                                sx={{
                                  width: 50,
                                  height: 50,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  border: '1px solid #ddd'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {product.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {product.description}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {product.points_cost > 0 ? `${product.points_cost} نقطة` : '-'}
                        </TableCell>
                        <TableCell>{getStatusChip(product.status)}</TableCell>
                        <TableCell>{formatDate(product.created_at)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="تعديل">
                              <IconButton
                                size="small"
                                onClick={() => handleProductEdit(product)}
                                sx={{ color: '#1976d2' }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="حذف">
                              <IconButton
                                size="small"
                                onClick={() => handleProductDelete(product.id)}
                                sx={{ color: '#d32f2f' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Orders Tab */}
        {tabValue === 1 && (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>رقم الطلب</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>العميل</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>المنتج</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>الكمية</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>المجموع</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>تاريخ التوصيل</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {giftOrdersLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : filteredOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                        لا توجد طلبات
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>{order.order_code}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2">{order.customer_name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {order.customer_phone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{order.product_name}</TableCell>
                        <TableCell>{order.quantity}</TableCell>
                        <TableCell>{formatPrice(order.total_price)}</TableCell>
                        <TableCell>{getStatusChip(order.status)}</TableCell>
                        <TableCell>{formatDate(order.delivery_date)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="عرض التفاصيل">
                              <IconButton 
                                size="small" 
                                sx={{ color: '#1976d2' }}
                                onClick={() => handleOrderView(order)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="تعديل">
                              <IconButton 
                                size="small" 
                                sx={{ color: '#ed6c02' }}
                                onClick={() => handleOrderEdit(order)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Container>

      {/* Add/Edit Product Dialog */}
      <Dialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingProduct ? 'تعديل المنتج' : 'إضافة منتج جديد'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="اسم المنتج"
                value={productForm.name}
                onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="الوصف"
                multiline
                rows={3}
                value={productForm.description}
                onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="تكلفة النقاط"
                type="number"
                value={productForm.points_cost}
                onChange={(e) => setProductForm(prev => ({ ...prev, points_cost: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>الحالة</InputLabel>
                <Select
                  value={productForm.status}
                  onChange={(e) => setProductForm(prev => ({ ...prev, status: e.target.value }))}
                  label="الحالة"
                >
                  <MenuItem value="active">نشط</MenuItem>
                  <MenuItem value="inactive">غير نشط</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <TextField
                  fullWidth
                  label="رابط الصورة"
                  value={productForm.image}
                  onChange={(e) => setProductForm(prev => ({ ...prev, image: e.target.value }))}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload-button"
                    type="file"
                    onChange={handleFileSelect}
                  />
                  <label htmlFor="image-upload-button">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={uploadingImage ? <CircularProgress size={20} /> : <UploadIcon />}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? 'جاري الرفع...' : 'رفع صورة'}
                    </Button>
                  </label>
                  {productForm.image && (
                    <Box
                      component="img"
                      src={productForm.image.startsWith('http') ? 
                        productForm.image : 
                        `${API_BASE_URL}/api/uploads/products/${productForm.image}`
                      }
                      alt="Product preview"
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid #ddd'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDialogOpen(false)}>
            إلغاء
          </Button>
          <Button
            onClick={handleProductSave}
            variant="contained"
            disabled={!productForm.name || !productForm.points_cost}
          >
            {editingProduct ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order View/Edit Dialog */}
      <Dialog
        open={orderDialogOpen}
        onClose={handleOrderDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {orderViewMode === 'view' ? 'تفاصيل الطلب' : 'تعديل الطلب'}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="رقم الطلب"
                  value={selectedOrder.gift_code || selectedOrder.order_code || ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="اسم العميل"
                  value={selectedOrder.customer_name || ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="رقم الهاتف"
                  value={selectedOrder.customer_phone || ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="المنتج"
                  value={selectedOrder.product_name || ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="تكلفة النقاط"
                  value={selectedOrder.points_cost || ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="تاريخ الطلب"
                  value={formatDate(selectedOrder.created_at)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>الحالة</InputLabel>
                  <Select
                    value={selectedOrder.status || 'pending'}
                    onChange={(e) => setSelectedOrder(prev => ({ ...prev, status: e.target.value }))}
                    label="الحالة"
                    disabled={orderViewMode === 'view'}
                  >
                    <MenuItem value="pending">في الانتظار</MenuItem>
                    <MenuItem value="confirmed">مؤكد</MenuItem>
                    <MenuItem value="delivered">تم التوصيل</MenuItem>
                    <MenuItem value="cancelled">ملغي</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {selectedOrder.processed_at && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="تاريخ المعالجة"
                    value={formatDate(selectedOrder.processed_at)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOrderDialogClose}>
            إلغاء
          </Button>
          {orderViewMode === 'edit' && (
            <Button
              onClick={() => handleOrderUpdate(selectedOrder)}
              variant="contained"
              disabled={!selectedOrder?.status}
            >
              تحديث
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      {tabValue === 0 && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
          }}
          onClick={() => setProductDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default GiftShopManagement;
